import { useRef, useState } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, FileDown, Upload, CheckCircle2, XCircle, RefreshCw, ListChecks, UploadCloud,
} from "lucide-react";

const IDIOMA_LABEL = { es: "Español", en: "Inglés", null: "Todos", undefined: "Todos" };

function FilaBadge({ fila }) {
  if (!fila.ok)
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title={fila.problemas.join(", ")}>
        <XCircle className="h-3.5 w-3.5" /> {fila.problemas[0]}
      </span>
    );
  if (fila.existe)
    return <span className="inline-flex items-center gap-1 text-[11px] text-blue-700"><RefreshCw className="h-3.5 w-3.5" /> Actualiza precio</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Nuevo</span>;
}

/**
 * Diálogo de carga masiva de tarifas (archivo CSV/Excel), reusado en:
 * - Configuración → Tours: tipo="tour" (tarifas BASE, cubre varios tours de una vez)
 * - Configuración → Agencias → Tarifas: tipo="agencia" agenciaId={id} (precios de esa agencia)
 * Columnas del archivo: Tour, Modalidad, Idioma (opcional), Precio Adulto, Precio Nino, Precio Infante.
 */
export default function ImportarTarifasDialog({ open, onOpenChange, tipo = "tour", agenciaId, onImported }) {
  const basePath = tipo === "agencia" ? `/agencias/${agenciaId}/tarifas/import` : "/tours/tarifas/import";
  const plantillaPath = tipo === "agencia" ? "/agencias/tarifas/import/plantilla" : "/tours/tarifas/import/plantilla";

  const [moneda, setMoneda] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [seleccion, setSeleccion] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setBatch(null);
    setSeleccion(new Set());
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const descargarPlantilla = async () => {
    try {
      await downloadFile(plantillaPath, "plantilla_tarifas.csv");
      toast.success("Plantilla descargada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const previewArchivo = async (file) => {
    if (!file) return;
    setLoading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("moneda", moneda || "USD");
      const { data } = await api.post(`${basePath}/preview`, fd);
      setBatch(data);
      setSeleccion(new Set((data.filas || []).filter((f) => f.ok).map((f) => f.row_id)));
      toast.success(`Vista previa lista: ${data.total} fila(s).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (rowId) => {
    setSeleccion((s) => {
      const c = new Set(s);
      if (c.has(rowId)) c.delete(rowId);
      else c.add(rowId);
      return c;
    });
  };

  const confirmar = async () => {
    if (!batch || seleccion.size === 0) {
      toast.error("Seleccioná al menos una fila para cargar.");
      return;
    }
    setConfirming(true);
    try {
      const { data } = await api.post(`${basePath}/batches/${batch.id}/confirmar`, {
        incluir_ids: Array.from(seleccion),
      });
      toast.success(`${data.aplicadas} tarifa(s) cargada(s)/actualizada(s)${data.omitidas ? ` · ${data.omitidas} omitida(s)` : ""}.`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const descartar = async () => {
    if (batch) {
      try {
        await api.post(`${basePath}/batches/${batch.id}/descartar`);
      } catch (e) { /* noop */ }
    }
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="importar-tarifas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-slate-700" />
            Carga masiva de tarifas {tipo === "agencia" ? "de la agencia" : "base (por tour)"}
          </DialogTitle>
          <DialogDescription>
            Subí un archivo CSV o Excel con el listado de precios (por ejemplo, el que pasó la cuenta de Bókun/agencia).
            Columnas: Tour, Modalidad, Idioma (opcional), Variante (opcional, solo para tours con SIN/CON BARCO como
            Cataratas del Niágara), Precio Adulto, Precio Niño, Precio Infante. Vas a poder revisar
            cada fila antes de guardar.
          </DialogDescription>
        </DialogHeader>

        {!batch ? (
          <div className="space-y-3">
            <div className="space-y-1.5 max-w-[140px]">
              <Label className="text-xs">Moneda del archivo</Label>
              <Input value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} maxLength={3} data-testid="importar-tarifas-moneda" />
            </div>
            <div className="flex items-center gap-2">
              <Input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => previewArchivo(e.target.files?.[0])}
                disabled={loading}
                data-testid="importar-tarifas-file-input"
              />
              {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400 shrink-0" />}
            </div>
            <p className="text-[11px] text-slate-400">
              <Upload className="h-3 w-3 inline" /> Las columnas se detectan automáticamente. Si no tenés el archivo armado,
              descargá la plantilla.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={descargarPlantilla}
              data-testid="importar-tarifas-descargar-plantilla"
              className="gap-2"
            >
              <FileDown className="h-4 w-4" /> Descargar plantilla (CSV)
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
              <div className="flex items-center gap-3 text-sm">
                <ListChecks className="h-4 w-4 text-slate-500" />
                <span className="text-slate-700">
                  {batch.total} filas · <span className="text-emerald-700">{batch.validas} válidas</span> ·{" "}
                  <span className="text-amber-700">{batch.con_problemas} con problemas</span>
                </span>
              </div>
              <span className="text-xs text-slate-500">{seleccion.size} seleccionadas</span>
            </div>
            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow className="hover:bg-white">
                      {["", "Tour", "Modalidad", "Idioma", "Variante", "Adulto", "Niño", "Infante", "Estado"].map((h, i) => (
                        <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.filas.map((f) => {
                      const n = f.normalizada;
                      return (
                        <TableRow
                          key={f.row_id}
                          data-testid={`importar-tarifas-row-${f.row_id}`}
                          className={!f.ok ? "bg-amber-50/30" : f.existe ? "bg-blue-50/30" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={seleccion.has(f.row_id)}
                              onCheckedChange={() => toggleRow(f.row_id)}
                              disabled={!f.ok}
                              data-testid={`importar-tarifas-row-check-${f.row_id}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm text-slate-800 max-w-[160px] truncate">
                            {f.tour?.nombre || n.tour || "—"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600 capitalize">{n.modalidad}</TableCell>
                          <TableCell className="text-sm text-slate-600">{IDIOMA_LABEL[n.idioma] || n.idioma}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {n.variante === "con_barco" ? "Con barco" : n.variante === "sin_barco" ? "Sin barco" : "—"}
                          </TableCell>
                          <TableCell className="text-sm font-mono text-slate-700">{n.precio_adulto}</TableCell>
                          <TableCell className="text-sm font-mono text-slate-700">{n.precio_nino}</TableCell>
                          <TableCell className="text-sm font-mono text-slate-700">{n.precio_infante}</TableCell>
                          <TableCell><FilaBadge fila={f} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={descartar} data-testid="importar-tarifas-descartar-button">
                Descartar
              </Button>
              <Button
                onClick={confirmar}
                disabled={confirming || seleccion.size === 0}
                data-testid="importar-tarifas-confirmar-button"
                className="gap-2"
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Guardar {seleccion.size || ""} tarifa(s)
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
